/**
 * 📦 packageOperations — EINZIGE QUELLE für Paket-Kernoperationen
 *
 * Damit Modal (AddPackageWithAI) UND Bench-Station exakt dieselben
 * Nebenwirkungen ausführen (Chronik, Tages-Statistik, Wartelisten-Matching)
 * und dieselbe Paket-Form erzeugen. So gibt es nichts doppelt zu pflegen.
 *
 * Was hier NICHT liegt: die UI-/State-gebundene Persistenz des Modals
 * (setPakete + autoSyncChanges) — die bleibt komponentenspezifisch. Für
 * Nicht-React-Aufrufer (Bench) gibt es createPackageStandalone().
 */
import { get, set, update } from 'idb-keyval';
import { savePaket, savePaketErgebnis, deletePaket, calculatePackagesHash } from '../packageSyncHelper';
import { isLoggedIn } from './apiService';
import { isCloudSyncAllowed, getCloudSyncStatus, istOnlinePaket } from '../utils/premiumSyncGuard';
import { logPaketHinzugefuegt, logPaketAusgegeben } from '../chronikHelper';
import { findMatchingWaitingCustomers } from '../waitingListHelper';
import * as serverPackageService from './serverPackageService';

/**
 * Berechnet den Paket-Hash aus dem lokalen Cache neu und pusht ihn zum Server.
 * WICHTIG: Ohne das ändert sich der Sync-Hash bei einer Bench-Ausgabe/-Änderung
 * NICHT → andere Geräte fragen „Hash gleich?" und bekommen „nichts zu syncen",
 * ziehen die Änderung also nie. (Der Server berechnet den Hash nicht selbst neu.)
 */
export async function refreshPackagesHash() {
  try {
    const list = (await get('pakete')) || [];
    const hash = await calculatePackagesHash(list);
    await set('packagesHash', hash);
    if (navigator.onLine && isLoggedIn()) {
      if (isCloudSyncAllowed()) {
        await set('lastPackageSync', Date.now());
        try { await serverPackageService.updateSyncHash(hash); }
        catch (e) { console.warn('📦 Sync-Hash-Push fehlgeschlagen:', e?.message); }
      } else if (getCloudSyncStatus().reason === 'expired') {
        const onlineHash = await calculatePackagesHash(list.filter(istOnlinePaket));
        try { await serverPackageService.updateSyncHash(onlineHash); }
        catch (e) { console.warn('📦 Sync-Hash-Push (Online-Bestand) fehlgeschlagen:', e?.message); }
      }
    }
  } catch (e) { console.warn('📦 Sync-Hash-Neuberechnung fehlgeschlagen:', e?.message); }
}

let flushInFlight = null;

/**
 * 🔄 Lädt alle lokal angelegten/geänderten, aber noch nicht bestätigten Pakete
 * (uploaded === false) auf den Server. Aufzurufen beim Wieder-Online-Gehen und
 * beim App-Start (online). Nutzt savePaket direkt und umgeht damit das
 * serverPakete-Cache-Sicherheitsnetz von syncSingleRequest. Idempotent & dedupliziert.
 * @returns {Promise<{flushed:number, total:number}>}
 */
export async function flushPendingPackages({ anwenden } = {}) {
  // anwenden(fn): wendet fn auf die AKTUELLE Paketliste der App an und speichert sie (23.10.2026). Ohne das wurde
  // ein vorher gelesener Stand zurückgeschrieben — entstand dazwischen ein neues Paket (Vinted-Scan), war es weg
  // (im Leistungstest mit 7.500 Paketen nachgewiesen: Speicher 7528 → 7527 aus genau dieser Stelle).
  if (flushInFlight) return flushInFlight;
  flushInFlight = (async () => {
    try {
      // Refresh-bewusst: getAuthUser() liefert bei abgelaufenem Access-Token null, obwohl ein
      // gültiger Refresh-Token vorliegt (savePaket refresht bei 401 selbst). Sonst no-oped der
      // Flush genau im Über-Nacht-Offline-Szenario, für das er gebaut ist.
      if (!navigator.onLine || !isLoggedIn()) return { flushed: 0, total: 0 };

      // 🗑️ Zuerst offline vorgemerkte Löschungen von Online-Paketen auf dem Server nachholen
      // (atomar per update(), damit parallel neu hinzukommende Lösch-IDs nicht verloren gehen)
      let geloescht = 0;
      try {
        const pendingDeletes = (await get('pending_delete_ids')) || [];
        if (Array.isArray(pendingDeletes) && pendingDeletes.length > 0) {
          const erledigt = new Set();
          for (const delId of pendingDeletes) {
            if (!delId) continue;
            const delOk = await deletePaket(delId);
            if (delOk) { erledigt.add(delId); geloescht++; }
          }
          if (erledigt.size > 0) {
            await update('pending_delete_ids', (alt) => (alt || []).filter((id) => !erledigt.has(id)));
          }
        }
      } catch (delErr) {
        console.warn('📤 Flush: Fehler beim Abarbeiten von pending_delete_ids:', delErr?.message);
      }

      const cloudAllowed = isCloudSyncAllowed();
      const list = (await get('pakete')) || [];
      // Im Lokal-Modus (!cloudAllowed) werden nur Änderungen an bereits bestehenden Online-Paketen
      // (Ausgabe, Rücksendung, Bearbeitung) zum Server geflusht; neue Pakete bleiben lokal.
      const pending = list.filter((p) => p && p.uploaded === false && (cloudAllowed || istOnlinePaket(p)));
      if (pending.length === 0) {
        if (geloescht > 0) await refreshPackagesHash();
        return { flushed: 0, total: 0 };
      }
      console.log(`📤 Flush: ${pending.length} ungesyncte Pakete werden hochgeladen…`);
      const geflushed = new Set();
      const vomServer = new Map(); // 409: Server-Version ist neuer → die übernehmen wir statt endlos neu zu senden
      let ok = 0;
      for (const p of pending) {
        const erg = await savePaketErgebnis(p);
        if (erg === 'ok') { geflushed.add(p.id); ok++; continue; }
        if (erg === 'konflikt') {
          try {
            const s = await serverPackageService.getPackage(p.id);
            if (s) vomServer.set(p.id, { ...serverPackageService.mapPackageFromServer(s), uploaded: true, inCloud: true });
          } catch (e) { console.warn(`📤 Flush: Server-Version von ${p.id} nicht ladbar:`, e?.message); }
        }
      }
      if (ok > 0 || vomServer.size > 0 || geloescht > 0) {
        // 🛡️ Lost-Update-Schutz: FRISCH lesen (ein paralleler Sync kann 'pakete' zwischenzeitlich
        // geschrieben haben) und nur die uploaded-Flags der geflushten IDs mergen — nicht den
        // alten Snapshot zurückschreiben.
        const umsetzen = (liste) => {
          let changed = false;
          const neu = (liste || []).map((p) => {
            if (!p || p.uploaded !== false) return p;
            if (geflushed.has(p.id)) { changed = true; return { ...p, uploaded: true, inCloud: true }; }
            if (vomServer.has(p.id)) { changed = true; return vomServer.get(p.id); }
            return p;
          });
          return changed ? neu : liste;
        };
        if (typeof anwenden === 'function') {
          await anwenden(umsetzen);
        } else {
          const frisch = (await get('pakete')) || [];
          const neu = umsetzen(frisch);
          if (neu !== frisch) await set('pakete', neu);
        }
        if (vomServer.size) console.warn(`📤 Flush: ${vomServer.size} veraltete lokale Kopie(n) durch die neuere Server-Version ersetzt`);
        if (ok > 0 || geloescht > 0) await refreshPackagesHash();  // Server-Hash nachziehen, sonst syncen andere Geräte nicht
      }
      console.log(`📤 Flush fertig: ${ok}/${pending.length} hochgeladen${vomServer.size ? `, ${vomServer.size} vom Server übernommen` : ''}`);
      return { flushed: ok, total: pending.length, uebernommen: vomServer.size };
    } catch (e) {
      console.warn('📤 Flush fehlgeschlagen:', e?.message);
      return { flushed: 0, total: 0 };
    }
  })();
  try {
    return await flushInFlight;
  } finally {
    flushInFlight = null;
  }
}

// JWT-basierte User-Erkennung (identisch zu AddPackageWithAI)
export function getAuthUser() {
  try {
    const token = localStorage.getItem('kiosk_jwt_access');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.exp * 1000 < Date.now()) return null;
    return { uid: payload.sub, email: payload.email };
  } catch {
    return null;
  }
}

// ============================================================
// 📊 Statistik: Der Client zählt NICHTS mehr selbst.
// Der Server bucht atomar an den Paket-Zustandsübergängen
// (server/lib/statsTracker.js) — dadurch keine Races, keine
// Doppelzählungen, Offline-Aktionen zählen genau einmal beim Sync.
// ============================================================

// ============================================================
// Paket-Form (einzige Quelle für die Feld-Struktur)
// ============================================================
export function buildNewPackage(data = {}) {
  const id = (window.crypto?.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).slice(2);
  const nowIso = new Date().toISOString();
  return {
    id,
    name: data.name || '',
    streetAndNumber: data.streetAndNumber || data.street || '',
    postalAndCity: data.postalAndCity || '',
    trackingNumber: data.trackingNumber || data.tracking || '',
    regalplatz: data.regalplatz || data.regal || '',
    gewicht: data.gewicht || '',
    paketFarbe: data.paketFarbe || data.farbe || '',
    paketAnzahl: data.paketAnzahl || 1,
    betrag: data.betrag || '',
    imageUrl: data.imageUrl || '', // ML-imageId → Server-Spalte image_url (Foto-Verknüpfung)
    absender: data.absender || '',
    ...(data.paketdienst ? { paketdienst: data.paketdienst } : {}), // 🚚 z. B. Foto-Station mit Vinted-/DHL-Anbindung
    datum: data.datum || nowIso,
    updatedAt: nowIso,
    lastUpdated: Date.now(),
    // 🔄 Sync-Marker: false = noch nicht auf dem Server bestätigt. Schützt das Paket
    // vor dem Full-Sync-Drop (packageSyncHelper.js:507) und markiert es für den
    // Reconnect-Flush. Wird nach erfolgreichem Server-Write auf true gesetzt.
    uploaded: false,
  };
}

// ============================================================
// Nebenwirkungen nach „Paket angelegt" — von BEIDEN aufgerufen
// → Chronik + Matching. Gibt wartende Kunden zurück.
// (Statistik zählt der Server beim Paket-Write selbst.)
// ============================================================
export async function recordPackageCreated(paket) {
  try {
    await logPaketHinzugefuegt(paket.id, paket.name, paket.absender, paket.trackingNumber);
  } catch (e) { console.warn('Chronik (hinzugefügt) fehlgeschlagen:', e?.message); }

  try {
    return await findMatchingWaitingCustomers(paket);
  } catch (e) {
    console.warn('Wartelisten-Matching fehlgeschlagen:', e?.message);
    return [];
  }
}

// Nebenwirkungen nach „Paket ausgegeben" — nur noch Chronik
// (Statistik zählt der Server beim Paket-Write selbst.)
export async function recordPackageHandedOut(paket, count = 1) {
  try {
    // Nur Name + Zeit in die Chronik — das Unterschrift-BILD bleibt am Paket (Server-Spalte abhol_unterschrift).
    // Sonst landet es dauerhaft in der Chronik, kommt bei jedem Chronik-Abruf auf alle Geräte und überlebt Rückgängig
    // und Löschen (Review 21.10.2026).
    const sig = (paket?.signatureDataUrl || paket?.abgeholtVonName)
      ? {
          abgeholtVonName: paket.abgeholtVonName || undefined,
          abholUnterschriftAm: paket.abholUnterschriftAm || undefined,
        }
      : undefined;
    await logPaketAusgegeben(paket.id, paket.name, paket.absender, paket.trackingNumber, sig);
  } catch (e) { console.warn('Chronik (ausgegeben) fehlgeschlagen:', e?.message); }
}

// ============================================================
// Komplettes Anlegen für Nicht-React-Aufrufer (Bench-Station)
// Server-Persistenz + lokaler Cache + Nebenwirkungen.
// ============================================================
/**
 * Aktualisiert ein bestehendes Paket im lokalen idb-Cache ('pakete'), damit
 * die Haupt-App-Liste Änderungen aus der Bench (ausgegeben, Regalplatz, Anzahl)
 * sofort sieht — savePaket schreibt nur auf den Server.
 */
export async function updatePackageCache(paket) {
  try {
    const list = (await get('pakete')) || [];
    const idx = list.findIndex((p) => p.id === paket.id);
    if (idx >= 0) list[idx] = { ...list[idx], ...paket };
    else list.push(paket);
    await set('pakete', list);
    // Sync-Hash mitziehen, damit andere Geräte die Änderung überhaupt syncen.
    await refreshPackagesHash();
  } catch (e) { console.warn('Lokaler Cache-Update fehlgeschlagen:', e?.message); }
}

/**
 * opts.onSaved(paket) — wird aufgerufen, SOBALD das Paket serverseitig sicher ist,
 * also noch vor Cache-Schreiben, Sync-Hash, Chronik und Wartelisten-Abgleich.
 * Dafür da, den Etikettendruck sofort zu starten: Der Druck braucht nur das Paket,
 * nicht den Rest der Kette — die kostet zusammen mehrere Sekunden (IndexedDB-Read/Write
 * der gesamten Liste, SHA-256 über alle Pakete, zweiter HTTP-Request für den Hash,
 * Chronik-Eintrag, Kunden-Matching) und hat den Druck bisher genau so lange blockiert.
 */
export async function createPackageStandalone(data, opts = {}) {
  const paket = buildNewPackage(data);

  // Server (Upsert) + lokaler IndexedDB-Cache konsistent halten.
  // savePaket wirft NIE (interner try/catch) und liefert false bei Netzfehler →
  // offline bleibt uploaded:false, sodass das Paket weder verloren geht noch als
  // synchronisiert gilt; der Reconnect-Flush lädt es später nach.
  const ok = await savePaket(paket);
  paket.uploaded = ok;
  if (ok) paket.inCloud = true;
  // Druck darf laufen, während wir aufräumen (auch offline: Paket ist lokal sicher).
  try { opts.onSaved?.(paket); } catch (e) { console.warn('onSaved-Hook:', e?.message); }
  try {
    const list = (await get('pakete')) || [];
    await set('pakete', [...list, paket]);
    // Sync-Hash mitziehen, damit andere Geräte das neue Paket syncen.
    await refreshPackagesHash();
  } catch (e) { console.warn('Lokaler Cache-Update fehlgeschlagen:', e?.message); }

  const matches = await recordPackageCreated(paket);
  return { paket, matches };
}
